import { Avatar, Box, Flex, HStack, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { CopyToClipboardPopover } from '@duke-hq/ui/components/CopyToClipboardPopover/CopyToClipboardPopover';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { useCallback } from 'react';
import { djs } from '@duke-hq/djs';
import { constructQueryParams, getUserFullName } from '@duke-hq/lib';
import { SortOrder, SortOrderSchema } from '@duke-hq/lib/pagination';
import { StudentsURLParams } from './types';

type StudentListItem = RouterOutputs['students']['all']['items'][number];

function StudentListRow({
  user,
  onClick,
  getProviderName,
}: {
  user: StudentListItem;
  onClick: (id: string) => void;
  getProviderName: (providerId: string) => string | undefined;
}) {
  const fullName = getUserFullName({
    first_name: user.first_name,
    last_name: user.last_name,
  });

  return (
    <Tr
      key={user.id}
      onClick={() => {
        onClick(user.id);
      }}
      cursor="pointer"
      _hover={{ bg: 'gray.50' }}
    >
      <Td>
        <HStack spacing="3">
          <Flex alignItems="center">
            <Avatar
              size="sm"
              mr={'12px'}
              name={fullName}
              src={user?.picture_url ?? undefined}
            ></Avatar>
            <Box>
              <Text fontWeight="medium">{fullName}</Text>
              <HStack spacing="1">
                <Text fontWeight="normal" color="fg.muted">
                  {user.email}
                </Text>
                {user.secondaryEmail != null ? (
                  <CopyToClipboardPopover value={user.secondaryEmail} />
                ) : null}
              </HStack>
            </Box>
          </Flex>
        </HStack>
      </Td>
      <Td>
        <Flex alignItems="start" direction={'column'}>
          {user.providerIds.length > 0 ? (
            <>
              {user.providerIds.map((providerId) => (
                <Text key={providerId} fontWeight="normal" color="fg.muted">
                  {getProviderName(providerId.toString())}
                </Text>
              ))}
              {user.hasMoreProviders ? (
                <Text fontWeight="normal" color="fg.muted">
                  <Trans>and more</Trans>
                </Text>
              ) : null}
            </>
          ) : (
            <Text fontWeight="normal" color="fg.muted">
              N/A
            </Text>
          )}
        </Flex>
      </Td>
      <Td>
        <Text fontWeight="normal" color="gray.600">
          {user.totalEnrolments || 0}
        </Text>
      </Td>
      <Td>
        {user?.last_activity_at ? (
          <Text color="fg.muted">{djs().to(djs(user.last_activity_at))}</Text>
        ) : (
          <Text color="fg.muted">
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
    </Tr>
  );
}

export function StudentList({
  students,
  urlParams,
  onFilters,
  getProviderName,
}: {
  students: StudentListItem[];
  urlParams: StudentsURLParams;
  onFilters: (filters: StudentsURLParams) => void;
  getProviderName: (providerId: string) => string | undefined;
}) {
  const navigate = useNavigate();
  const { orderBy } = urlParams;
  const sort = SortOrderSchema.safeParse(urlParams.sort).data;

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        ...urlParams,
        orderBy: name,
        sort: newSortDirection,
      };

      onFilters(updatedFilters as StudentsURLParams);
    },
    [sort, urlParams, onFilters],
  );

  const handleClick = useCallback(
    (id: string | number) => {
      const baseUrl = `/student/${id}`;
      navigate(`${baseUrl}${constructQueryParams({ ...urlParams })}`);
    },
    [navigate, urlParams],
  );

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <SortableHeader
                label={t`Student`}
                columnName="name"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Provider</Trans>
            </Th>
            <Th>
              <SortableHeader
                label={t`Enrolments`}
                columnName="enrolments"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Last activity`}
                columnName="last_activity_at"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {Array.isArray(students) && students.length !== 0
            ? students.map((user: StudentListItem) => (
                <StudentListRow
                  key={user.id}
                  user={user}
                  onClick={handleClick}
                  getProviderName={getProviderName}
                />
              ))
            : null}
        </Tbody>
      </Table>

      {Array.isArray(students) && students.length === 0 ? (
        <Flex
          fontSize="sm"
          w="full"
          m={6}
          color="gray.600"
          fontWeight="medium"
          justifyContent="center"
        >
          <Trans>No results found</Trans>
        </Flex>
      ) : null}
    </>
  );
}
